import React from 'react';
import PropTypes from 'prop-types';

import Browser from '@wetransfer/concorde-browser';

import Analytics from '@module/analytics';
import I18n from '@module/i18n';
import Router from '@module/router';

import Spinner from '@component/spinner';
export class Progress extends React.Component {
  constructor(props) {
    super(props);

    this.cancelTransfer = this.cancelTransfer.bind(this);
    this.toggleCancelConfirmation = this.toggleCancelConfirmation.bind(this);
  }

  // This component is mounted during transfer, so we'll add this window hook
  // asking if the user is sure when closing the window / navigating away.
  componentDidMount() {
    window.onbeforeunload = () => {
      try {
        this.props.abort(true);
      } catch (e) {
        /* we don't need this catch. */
      }

      // Ask if the user is sure about leaving the page.
      return 'Are you sure you want to cancel this transfer?';
    };
  }

  // Remove message about aborting transfer.
  componentWillUnmount() {
    window.onbeforeunload = null;
  }

  // The user wants to abort the current transfer.
  cancelTransfer() {
    const { cancel, transfer } = this.props;
    Analytics.pushDataLayerEvent({
      category: 'Transfer',
      action: 'Cancelled',
      label: transfer.progress.duration,
      value: transfer.totalSize,
    });

    cancel();
  }

  toggleCancelConfirmation() {
    if (this.props.transfer.state === 'started') {
      return this.props.pause();
    }

    return this.props.resume();
  }

  // Get the remaining text translated
  getRemainingText(progress) {
    if (isNaN(progress.remaining) || progress.remaining === false) {
      return false;
    }

    let minutes = Math.ceil(progress.remaining / 60);
    if (minutes < 60) {
      // This is how the old monolith calculated minutes
      minutes = minutes < 1 ? 0 : Math.round(minutes);
      return I18n.t('transfer.progress.remaining.minutes', { count: minutes });
    }

    return I18n.t('transfer.progress.remaining.hours', {
      count: Math.round(minutes / 60),
    });
  }

  renderButtons(transfer) {
    if (transfer.state === 'paused') {
      return [
        <div key="transfer_button" className="transfer__button-container">
          <button
            onClick={this.toggleCancelConfirmation}
            className={
              'transfer__button transfer__button--cancel transfer__button--alt'
            }
          >
            {I18n.t('transfer.buttons.cancel_no')}
          </button>
          <button
            onClick={this.cancelTransfer}
            className={'transfer__button transfer__button--cancel'}
          >
            {I18n.t('transfer.buttons.cancel_yes')}
          </button>
        </div>,
      ];
    }

    const classNames = ['transfer__button transfer__button--alt'];
    let onClick = this.toggleCancelConfirmation;

    // Don't allow cancel until the transfer has an id
    const { sent, total } = transfer.progress;
    if (!transfer.id || sent === total) {
      classNames.push('transfer__button--alt-inactive');
      onClick = null;
    }

    return [
      <button
        key="transfer_button_cancel"
        onClick={onClick}
        className={classNames.join(' ')}
      >
        {I18n.t('transfer.buttons.cancel')}
      </button>,
    ];
  }

  openPanel(event) {
    event.preventDefault();
    event.stopPropagation();

    Analytics.pushDataLayerEvent({
      category: 'Transfer',
      action: 'Progress Panel',
    });
    Router.navigateTo('/transfer/progress', Router.SILENT);
  }

  getMetaInfo(transfer) {
    let info = '';

    if (transfer.type === 'link') {
      info = I18n.t('transfer.progress.meta.link', {
        count: transfer.files.length,
      });
    } else if (transfer.type === 'email') {
      let filesKey = 'other_files';
      if (transfer.files.length === 1) {
        filesKey = 'one_file';
      }

      let recipientsKey = 'other_recipients';
      if (transfer.recipients.length === 1) {
        recipientsKey = 'one_recipient';
      }

      info = I18n.t(
        `transfer.progress.meta.email.${filesKey}_${recipientsKey}`,
        {
          filesCount: transfer.files.length,
          recipientsCount: transfer.recipients.length,
        }
      );
    }

    if (Browser.isMobile) {
      return info;
    }

    return (
      <a href="/transfer/progress" onClick={this.openPanel}>
        {info}
      </a>
    );
  }

  // Let's render our progress screen (spinner?) and pause buttons.
  render() {
    const {
      transfer,
      layout: {
        wallpaper: { uploadSpinnerColor, uploadSpinnerTransitionColor },
      },
    } = this.props;
    const buttons = (
      <div className="transfer__footer">{this.renderButtons(transfer)}</div>
    );
    const paused = transfer.state === 'paused';
    const title = paused ? 'title_cancel' : 'title';

    // Build our time remaining and bytes sent labels
    let metaInfo = <p>{this.getMetaInfo(transfer)}</p>;
    let remainingTime = <p>{this.getRemainingText(transfer.progress)}</p>;
    let bytesSent = (
      <p>
        {I18n.t('transfer.progress.uploaded', {
          total: I18n.size(transfer.progress.total, { precision: 1 }),
          sent: I18n.size(transfer.progress.sent, { precision: 1 }),
        })}
      </p>
    );
    let spinnerForeground = undefined;

    // We don't show time remaining etc. during cancel confirmation screen
    if (paused) {
      remainingTime = bytesSent = metaInfo = null;
    }

    if (uploadSpinnerColor && /^#[0-9A-F]{6}$/i.test(uploadSpinnerColor)) {
      spinnerForeground = uploadSpinnerColor;
    }

    if (
      uploadSpinnerTransitionColor &&
      /^#[0-9A-F]{6}$/i.test(uploadSpinnerTransitionColor)
    ) {
      spinnerForeground = uploadSpinnerTransitionColor;
    }

    return (
      <div className="transfer__window uploader--progress">
        <div className="transfer__contents">
          <Spinner
            className="transfer__spinner"
            rotate={true}
            foreground={spinnerForeground}
            percentage={transfer.progress.percentage}
          />
          <h2>{I18n.t(`transfer.progress.${title}`)}</h2>
          {metaInfo}
          {bytesSent}
          {remainingTime}
        </div>
        {buttons}
      </div>
    );
  }
}

Progress.propTypes = {
  abort: PropTypes.func.isRequired,
  cancel: PropTypes.func.isRequired,
  resume: PropTypes.func.isRequired,
  pause: PropTypes.func.isRequired,
  transfer: PropTypes.object.isRequired,
  layout: PropTypes.object.isRequired,
};

export default Progress;
